/**
 * ChatbotConfigCard — admin surface for renaming the chatbot.
 *
 * Persists the change to `/api/admin/chatbot/config` and invalidates the
 * public `chatbot-config` query so the widget header, /sancta page, and
 * every reference across the SPA updates immediately.
 */
import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Save, RotateCcw, Bot } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

interface Cfg {
  name: string;
  tagline: string;
  greeting: string;
  returning_greeting: string;
  accent_color: string;
  emoji_prefix: string;
}

export const ChatbotConfigCard: React.FC = () => {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const readonly = (me?.role || "user") !== "admin";

  const q = useQuery<Cfg>({
    queryKey: ["admin-chatbot-config"],
    queryFn: async () => (await api.get("/admin/chatbot/config")).data,
  });

  const [draft, setDraft] = React.useState<Cfg | null>(null);
  const [dirty, setDirty] = React.useState(false);
  React.useEffect(() => { if (q.data && !draft) setDraft(q.data); }, [q.data, draft]);

  const patch = (k: keyof Cfg, v: string) => {
    if (!draft) return;
    setDraft({ ...draft, [k]: v });
    setDirty(true);
  };

  const save = useMutation({
    mutationFn: async () => (await api.put("/admin/chatbot/config", draft)).data,
    onSuccess: (data) => {
      setDraft(data);
      setDirty(false);
      toast.success(`Chatbot renamed to ${data.name} — reflected everywhere.`);
      qc.invalidateQueries({ queryKey: ["admin-chatbot-config"] });
      qc.invalidateQueries({ queryKey: ["chatbot-config"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const reset = useMutation({
    mutationFn: async () => (await api.post("/admin/chatbot/config/reset")).data,
    onSuccess: (data) => {
      setDraft(data);
      setDirty(false);
      toast.success("Restored default chatbot identity.");
      qc.invalidateQueries({ queryKey: ["admin-chatbot-config"] });
      qc.invalidateQueries({ queryKey: ["chatbot-config"] });
    },
    onError: () => toast.error("Reset failed"),
  });

  if (q.isLoading || !draft) {
    return <div className="sg-card p-6 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading chatbot config…</div>;
  }
  return (
    <div className="sg-card p-6 space-y-4" data-testid="admin-chatbot-config">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-fuchsia-500 to-orange-500 grid place-items-center text-white shrink-0">
          <Bot className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display font-bold text-lg">Chatbot Identity</div>
          <div className="text-xs text-slate-500 mt-0.5">
            Rename the AI concierge. Every reference across the platform — widget header, /sancta full page,
            LLM self-identity, greeting copy — updates instantly on save.
          </div>
        </div>
        <Button onClick={() => reset.mutate()} disabled={readonly || reset.isPending} variant="outline" size="sm" data-testid="chatbot-config-reset-btn">
          <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Reset
        </Button>
        <Button onClick={() => save.mutate()} disabled={readonly || !dirty || save.isPending} size="sm" data-testid="chatbot-config-save-btn">
          {save.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
          {dirty ? "Save" : "Saved"}
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Name" hint="Shown in the widget header and everywhere. Max 40 chars.">
          <input
            value={draft.name}
            onChange={(e) => patch("name", e.target.value)}
            className="input w-full"
            maxLength={40}
            disabled={readonly}
            data-testid="chatbot-config-name"
          />
        </Field>
        <Field label="Tagline" hint="One-liner under the name.">
          <input
            value={draft.tagline}
            onChange={(e) => patch("tagline", e.target.value)}
            className="input w-full"
            maxLength={140}
            disabled={readonly}
            data-testid="chatbot-config-tagline"
          />
        </Field>
        <Field label="Emoji prefix" hint="Warm-touch emoji sprinkled into replies.">
          <input
            value={draft.emoji_prefix}
            onChange={(e) => patch("emoji_prefix", e.target.value)}
            className="input w-full"
            maxLength={8}
            disabled={readonly}
            data-testid="chatbot-config-emoji"
          />
        </Field>
        <Field label="Accent color" hint="CSS hex color for the widget gradient.">
          <input
            value={draft.accent_color}
            onChange={(e) => patch("accent_color", e.target.value)}
            className="input w-full"
            maxLength={16}
            disabled={readonly}
            data-testid="chatbot-config-accent"
          />
        </Field>
      </div>
      <Field label="Greeting (first-time)" hint="Use {name} placeholder to inline the current chatbot name.">
        <textarea
          value={draft.greeting}
          onChange={(e) => patch("greeting", e.target.value)}
          rows={2}
          className="input w-full"
          maxLength={600}
          disabled={readonly}
          data-testid="chatbot-config-greeting"
        />
      </Field>
      <Field label="Returning-user greeting" hint="Used when the shopper has past sessions on file.">
        <textarea
          value={draft.returning_greeting}
          onChange={(e) => patch("returning_greeting", e.target.value)}
          rows={2}
          className="input w-full"
          maxLength={600}
          disabled={readonly}
          data-testid="chatbot-config-returning-greeting"
        />
      </Field>
    </div>
  );
};

const Field: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({ label, hint, children }) => (
  <div>
    <div className="text-xs font-semibold text-slate-900 dark:text-white mb-0.5">{label}</div>
    {hint && <div className="text-[10px] text-slate-500 dark:text-slate-400 mb-1.5">{hint}</div>}
    {children}
  </div>
);

export default ChatbotConfigCard;
